/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, {
  DEFAULT_FONT_SIZE,
  PADDING_VERTICAL,
} from './WireframeBase';
import tools from '../tools';
import wireframeBreadcrumb from '../assets/icons/wireframeBreadcrumb.svg';
import wireframeStyleBreadcrumbSlash from '../assets/icons/wireframeStyleBreadcrumbSlash.svg';
import wireframeStyleBreadcrumbAngle from '../assets/icons/wireframeStyleBreadcrumbAngle.svg';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { SPACER_2 } from '../utils/style';
import {
  WIREFRAME_PRIMARY_COLOR,
  WIREFRAME_SELECTION_COLOR,
} from '../utils/wireframes';
import { InputField } from '../components/wireframes/InputField';
import WireframeAddItemControl from '../controls/WireframeAddItemControl';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { Icon } from '../canvasx/Icon';
import { ANGLE_SEPARATOR, SLASH_SEPARATOR } from '../objects/IconsHelper';

const CRUMB_SPACING = SPACER_2;
const ICON_OPACITY = 0.3;
class WireframeBreadcrumb extends WireframeBase {
  hasDraggableItems = true;

  initItems() {
    if (this.fabricObject.items.items.length === 0) {
      this.fabricObject.items.add({
        value: 'Home',
        iconLeft: {
          prefix: 'fas',
          iconName: 'house',
        },
      });
      this.fabricObject.items.add({
        value: 'Collection',
        iconLeft: {
          prefix: 'fas',
          iconName: 'layer-group',
        },
      });
      this.fabricObject.items.add({
        value: 'Product',
        iconLeft: {
          prefix: 'fas',
          iconName: 'box',
        },
      });
    }
  }

  tools() {
    if (this.activeKey) {
      const itemTools = [
        [tools.ToolWireframeItemState],
        [tools.ToolWireframeIcon],
      ];

      // Show delete tool only if more than two items left
      if (this.fabricObject.items.items.length > 2) {
        itemTools.push([tools.ToolWireframeDeleteItem]);
      }

      return itemTools;
    }
    return [
      [tools.ToolWireframeAddItem],
      [tools.ToolWireframeComponentStyle],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
    ];
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      addItem: new WireframeAddItemControl({ layout: 'row' }),
    };
  }

  static defaultWidth(): number {
    return 255;
  }

  static defaultHeight(): number {
    return 40;
  }

  static iconSvg() {
    return wireframeBreadcrumb;
  }

  static externalName(): string {
    return 'Breadcrumb';
  }

  static componentStyles() {
    return {
      slash: wireframeStyleBreadcrumbSlash,
      angle: wireframeStyleBreadcrumbAngle,
    };
  }

  componentStyles() {
    return WireframeBreadcrumb.componentStyles();
  }

  defaultText() {
    return 'Section';
  }

  supportedIcons(): string[] {
    return ['iconLeft'];
  }

  setIcon(location: any, icon: any): void {
    const item = this.activeItem();
    // If this item has no text and the icon is removed, delete the item
    if (item && icon === null && !item.value) {
      this.deleteItem();
      return;
    }

    super.setIcon(location, icon);
  }

  get slashSeparator() {
    return this.fabricObject.style === 'slash';
  }

  wasSet(keyOrPath: KeyPath, value: any): void {
    if (keyOrPath === 'textEditing' && !value) {
      // Remove empty items when user finishes editing
      this.fabricObject.items.items.forEach(item => {
        if (!item.value && !item.iconLeft) {
          this.deleteItem(item.id);
        }
      });
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const { scaleX, items, fontSize = DEFAULT_FONT_SIZE } = this.fabricObject;
    const slash = this.slashSeparator;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const iconSize = fontSize + 2;

    let renderedDimensions;

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexRow
            verticalAlignment='center'
            gap={CRUMB_SPACING / 2}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
          >
            {items.items.map((item, i) => {
              const active = this.activeKey.includes(item.id);

              return (
                <FlexRow
                  key={item.id}
                  verticalAlignment='center'
                  gap={CRUMB_SPACING / 2}
                  setRenderedDimensions={dimensions => {
                    this.updateMaxItemDimensions({
                      dimensions,
                      useUnwrappedWidth: true,
                    });
                    item.dimensions = dimensions;
                  }}
                >
                  <InputField
                    wireframe={this}
                    item={item}
                    color={fill}
                    iconColor={fill}
                    iconSize={iconSize}
                    textKey={`text:${item.id}`}
                    size={fontSize}
                    padding={{
                      vertical: PADDING_VERTICAL + (item.iconLeft ? 0 : 1),
                      horizontal: CRUMB_SPACING / 2,
                    }}
                    border={active ? WIREFRAME_SELECTION_COLOR : 'transparent'}
                    onChange={value => {
                      this.onItemChange(value);
                    }}
                  />
                  {i < items.items.length - 1 && (
                    <Icon
                      icon={slash ? SLASH_SEPARATOR : ANGLE_SEPARATOR}
                      iconFill={fill}
                      opacity={ICON_OPACITY}
                    />
                  )}
                </FlexRow>
              );
            })}
          </FlexRow>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeBreadcrumb;
